/**
 * Formats an amount in minor units as money — e.g. 104110 with 'eur' becomes '1.041,10 €'.
 *
 * The licensing service and the payment provider both quote amounts in minor units (cents), so the
 * division belongs in one place rather than at each call site, where a missing `/ 100` silently
 * shows a hundredfold figure on a payment confirmation.
 *
 * Grouping and decimal separators follow the currency's own market rather than the UI language: a
 * euro price reads '1.041,10 €' and a dollar price '$1,041.10', which is what each audience expects
 * and what the approved design shows. Tying it to the UI locale instead would render euro prices as
 * '€1,041.10' for an English-speaking clinic in Europe.
 *
 * @param minorUnits Amount in the currency's minor units.
 * @param currency ISO currency code as the service reports it, e.g. 'eur'.
 * @returns The formatted amount, falling back to a plain two-decimal figure with the raw code for a
 * currency this does not know — visibly odd rather than silently wrong.
 */
export function formatMoney(minorUnits: number, currency: string): string {
    const amount = minorUnits / 100;
    const code = (currency || '').toLowerCase();

    // Only the two markets the payment packages support (AccountType.Europe / .Usa).
    const locales: Record<string, string> = { eur: 'de-DE', usd: 'en-US' };
    const locale = locales[code];

    if (!locale) {
        return `${amount.toFixed(2)} ${currency?.toUpperCase() ?? ''}`.trim();
    }

    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code.toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}
